import { useMemo, useState } from 'react';
import { Select } from '@busbook/ui';
import { autoSeatCode, layoutCapacity, type SeatCell, type SeatCellType } from '@busbook/shared';

/**
 * Visual seat-layout grid (docs/06 "/fleet"): set rows × columns, then click a
 * cell to cycle it through seat → aisle → door → driver. Seat numbers renumber
 * automatically so an aisle never leaves a gap in the seat names.
 */

const CYCLE: SeatCellType[] = ['SEAT', 'AISLE', 'DOOR', 'DRIVER'];

const CELL_STYLE: Record<SeatCellType, { bg: string; fg: string; label: string }> = {
  SEAT: { bg: 'var(--color-primary-wash)', fg: 'var(--color-primary)', label: '' },
  AISLE: { bg: 'transparent', fg: 'var(--color-faint)', label: '·' },
  DOOR: { bg: 'color-mix(in srgb, var(--signal-delayed) 14%, transparent)', fg: 'var(--signal-delayed)', label: 'D' },
  DRIVER: { bg: 'color-mix(in srgb, var(--color-text) 10%, transparent)', fg: 'var(--color-muted)', label: '⚙' },
};

/** Fixed palette a tier's id is hashed into, so its colour stays stable across renders. */
const TIER_PALETTE = [
  '#2563eb', // blue
  '#16a34a', // green
  '#d97706', // amber
  '#db2777', // pink
  '#7c3aed', // violet
  '#0d9488', // teal
];

function hashTierColor(tierId: string): string {
  let hash = 0;
  for (let i = 0; i < tierId.length; i++) {
    hash = (hash * 31 + tierId.charCodeAt(i)) >>> 0;
  }
  return TIER_PALETTE[hash % TIER_PALETTE.length]!;
}

function TierSwatch({ color }: { color: string }) {
  return (
    <span
      className="inline-block h-3 w-3 rounded-full border"
      style={{ background: color, borderColor: 'var(--color-line)' }}
      aria-hidden="true"
    />
  );
}

/** Renumbers seats left-to-right within each row, skipping non-seat cells. */
export function renumber(cells: SeatCell[], rows: number, columns: number): SeatCell[] {
  const byPosition = new Map(cells.map((cell) => [`${cell.row}:${cell.col}`, cell]));
  const next: SeatCell[] = [];

  for (let row = 0; row < rows; row++) {
    let seatIndex = 0;
    for (let col = 0; col < columns; col++) {
      const existing = byPosition.get(`${row}:${col}`);
      const type = existing?.type ?? 'SEAT';
      next.push({
        row,
        col,
        type,
        code: type === 'SEAT' ? autoSeatCode(row, seatIndex++) : '',
        ...(type === 'SEAT' && existing?.tierId ? { tierId: existing.tierId } : {}),
      });
    }
  }
  return next;
}

export function SeatLayoutEditor({
  rows,
  columns,
  seats,
  onChange,
  tiers,
  readOnly = false,
}: {
  rows: number;
  columns: number;
  seats: SeatCell[];
  onChange: (seats: SeatCell[]) => void;
  tiers: Array<{ id: string; name: string; isDefault: boolean }>;
  readOnly?: boolean;
}) {
  const grid = useMemo(() => renumber(seats, rows, columns), [seats, rows, columns]);
  const capacity = layoutCapacity(grid);
  const [armedTierId, setArmedTierId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<string | null>(null);

  const defaultTierId = tiers.find((t) => t.isDefault)?.id;
  const tierColor = (tierId: string) => hashTierColor(tierId);
  const cellTierColor = (cell: SeatCell) => {
    if (cell.tierId) return tierColor(cell.tierId);
    return defaultTierId ? tierColor(defaultTierId) : 'var(--color-faint)';
  };

  function cycle(row: number, col: number) {
    if (readOnly) return;
    const next = grid.map((cell) => {
      if (cell.row !== row || cell.col !== col) return cell;
      if (armedTierId !== null && cell.type === 'SEAT') {
        return {
          ...cell,
          tierId: armedTierId === defaultTierId ? undefined : armedTierId,
        };
      }
      const index = CYCLE.indexOf(cell.type);
      return { ...cell, type: CYCLE[(index + 1) % CYCLE.length]! };
    });
    onChange(renumber(next, rows, columns));
  }

  function setCellTier(row: number, col: number, tierId: string) {
    const next = grid.map((cell) => {
      if (cell.row !== row || cell.col !== col) return cell;
      return { ...cell, tierId: tierId === defaultTierId ? undefined : tierId };
    });
    onChange(renumber(next, rows, columns));
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium">Layout</p>
        <p className="text-xs tabular" style={{ color: 'var(--color-muted)' }}>
          {capacity} seat{capacity === 1 ? '' : 's'}
        </p>
      </div>

      {!readOnly && tiers.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              type="button"
              onClick={() => setArmedTierId((current) => (current === tier.id ? null : tier.id))}
              aria-pressed={armedTierId === tier.id}
              className="flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-medium"
              style={{
                borderColor: armedTierId === tier.id ? tierColor(tier.id) : 'var(--color-line)',
                background: armedTierId === tier.id ? 'color-mix(in srgb, ' + tierColor(tier.id) + ' 14%, transparent)' : 'transparent',
              }}
            >
              <TierSwatch color={tierColor(tier.id)} />
              {tier.name}
            </button>
          ))}
        </div>
      )}

      <div
        className="mt-2 inline-block rounded-xl border p-3"
        style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      >
        {/* The front of the bus is at the top, matching how an operator sees it. */}
        <p className="mb-2 text-center text-[0.625rem] uppercase tracking-widest" style={{ color: 'var(--color-faint)' }}>
          Front
        </p>
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(${columns}, 2.25rem)` }}
          role="group"
          aria-label="Seat grid"
        >
          {grid.map((cell) => {
            const style = CELL_STYLE[cell.type];
            const cellKey = `${cell.row}:${cell.col}`;
            return (
              <div key={cellKey} style={{ position: 'relative' }}>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => cycle(cell.row, cell.col)}
                  aria-label={`Row ${cell.row + 1}, column ${cell.col + 1}: ${cell.type.toLowerCase()}${
                    cell.code ? ` (${cell.code})` : ''
                  }`}
                  className="grid h-9 w-9 place-items-center rounded-md border text-[0.625rem] font-semibold tabular disabled:cursor-default"
                  style={{
                    background: style.bg,
                    color: style.fg,
                    borderColor: cell.type === 'AISLE' ? 'transparent' : 'var(--color-line)',
                  }}
                >
                  {cell.code || style.label}
                </button>
                {cell.type === 'SEAT' && !readOnly && tiers.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCell((current) => (current === cellKey ? null : cellKey));
                      }}
                      aria-label={`Change tier for seat ${cell.code}`}
                      className="h-2.5 w-2.5 rounded-full border"
                      style={{
                        position: 'absolute',
                        top: 1,
                        right: 1,
                        background: cellTierColor(cell),
                        borderColor: 'var(--color-surface)',
                      }}
                    />
                    {editingCell === cellKey && (
                      <div
                        style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select
                          autoFocus
                          value={cell.tierId ?? defaultTierId ?? ''}
                          onChange={(e) => {
                            setCellTier(cell.row, cell.col, e.target.value);
                            setEditingCell(null);
                          }}
                          onBlur={() => setEditingCell(null)}
                          className="h-7 w-24 text-xs"
                        >
                          {tiers.map((tier) => (
                            <option key={tier.id} value={tier.id}>
                              {tier.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {tiers.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {tiers.map((tier) => (
            <span key={tier.id} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-muted)' }}>
              <TierSwatch color={tierColor(tier.id)} />
              {tier.name}
            </span>
          ))}
        </div>
      )}

      {!readOnly && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-muted)' }}>
          Click a cell to cycle seat → aisle → door → driver. Seat numbers update automatically.
          {tiers.length > 0 && ' Arm a tier above to paint it onto seats, or use a seat\'s corner dot to set one seat at a time.'}
        </p>
      )}
    </div>
  );
}
